import { useCallback, useState } from 'react'

import { Box, Button, Typography } from '@percent/lemonade'
import { QuestionCard } from '../question-card/question-card'

import type {
  SurveyQuestion,
  SurveyQuestionWithEditMeta,
} from '../../../dtos/survey.dto'
import { SurveyQuestionType } from '../../../dtos/survey.dto'
import styles from './question-builder.module.scss'
import {
  MAX_QUESTIONS,
  QuestionBuilderProps,
  QuestionBuilderTextProps,
} from './question-builder.types'

export function QuestionBuilder({
  questions,
  onChange,
  errors,
  touched,
  setFieldTouched,
  headerTitle,
  headerDescription,
  addQuestionButton,
  defaultRatingMinLabel,
  defaultRatingMaxLabel,
  typeLabel,
  questionLabel,
  requiredLabel,
  questionPreviewLabel,
  selectTypePlaceholder,
  ratingQuestionPlaceholder,
  freeTextQuestionPlaceholder,
  removeButton,
  ratingTypeLabel,
  freeTextTypeLabel,
  activeBadgeLabel,
  deactivatedBadgeLabel,
  questionResponseSummaryByIndex,
  deactivateButtonLabel,
  activateButtonLabel,
  onDeactivateRequest,
  onActivateRequest,
  allowedQuestionTypes,
  maxQuestions,
  questionsFieldName = 'surveyQuestions',
  lastQuestionRemoval = 'deactivate',
}: Readonly<QuestionBuilderProps & QuestionBuilderTextProps>) {
  const questionLimit = maxQuestions ?? MAX_QUESTIONS
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [expandedDeactivatedIndices, setExpandedDeactivatedIndices] = useState<
    Set<number>
  >(new Set())

  const isDeactivated = useCallback((q: SurveyQuestion) => {
    const meta = q as SurveyQuestionWithEditMeta
    return meta.active === false || Boolean(meta.disabledAt)
  }, [])

  const toggleCollapsed = useCallback((index: number) => {
    setExpandedDeactivatedIndices((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const handleDeactivate = (index: number) => {
    handleUpdateQuestion(index, {
      active: false,
      disabledAt: new Date().toISOString(),
    } as Partial<SurveyQuestion>)
  }

  const handleActivate = (index: number) => {
    handleUpdateQuestion(index, {
      active: true,
      disabledAt: undefined,
    } as Partial<SurveyQuestion>)
  }

  const handleAddQuestion = () => {
    if (questions.length >= questionLimit) return

    const defaultType = allowedQuestionTypes?.[0] ?? SurveyQuestionType.Rating

    const newQuestion: SurveyQuestion =
      defaultType === SurveyQuestionType.FreeText
        ? { type: SurveyQuestionType.FreeText, label: '', required: false }
        : {
            type: SurveyQuestionType.Rating,
            label: '',
            required: false,
            config: {
              min: 1,
              max: 10,
              minLabel: defaultRatingMinLabel,
              maxLabel: defaultRatingMaxLabel,
            },
          }

    onChange([...questions, newQuestion])
    setEditingIndex(questions.length)
  }

  const handleRemoveQuestion = (index: number) => {
    if (questions.length === 1) {
      if (lastQuestionRemoval === 'clear') {
        onChange([])
        if (editingIndex === index) {
          setEditingIndex(null)
        }
        return
      }
      // With a single question, "Remove" would send `questions: []` and break re-enabling the survey
      // (form re-seeds defaults) in edit mode. Keep the row and deactivate (active: false) so the API
      // payload stays a full list with flags, not an empty array.
      handleDeactivate(index)
      return
    }
    onChange(questions.filter((_, i) => i !== index))
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  const handleUpdateQuestion = (
    index: number,
    updates: Partial<SurveyQuestion> & { active?: boolean; disabledAt?: string }
  ) => {
    onChange(
      questions.map((q, idx) => {
        if (idx !== index) return q

        // Handle type change
        if (updates.type && updates.type !== q.type) {
          if (updates.type === SurveyQuestionType.Rating) {
            const ratingUpdates = updates as Partial<
              Extract<SurveyQuestion, { type: SurveyQuestionType.Rating }>
            >
            return {
              id: q.id,
              type: SurveyQuestionType.Rating,
              label: q.label,
              required: q.required,
              config: ratingUpdates.config || {
                min: 1,
                max: 10,
                minLabel: defaultRatingMinLabel,
                maxLabel: defaultRatingMaxLabel,
              },
            } as SurveyQuestion
          } else {
            return {
              id: q.id,
              type: SurveyQuestionType.FreeText,
              label: q.label,
              required: q.required,
            } as SurveyQuestion
          }
        }

        // Same type, just update fields
        return { ...q, ...updates } as SurveyQuestion
      })
    )
  }

  return (
    <Box className={styles.QuestionBuilderWrapper}>
      <Box className={styles.Header}>
        <Typography variant="h4" $color={'text_primary'}>
          {headerTitle}
        </Typography>
        <Typography variant="default" $color={'gray400'}>
          {headerDescription}
        </Typography>
      </Box>

      {questions.map((question, index) => {
        const meta = question as SurveyQuestionWithEditMeta
        const deactivated = isDeactivated(question)
        const responseSummaryText =
          questionResponseSummaryByIndex?.[index] ?? null
        return (
          <QuestionCard
            key={`question-${index}-${question.id ?? ''}`}
            question={question}
            index={index}
            isEditing={editingIndex === index}
            onEdit={() => setEditingIndex(index)}
            onRemove={() => handleRemoveQuestion(index)}
            onUpdate={(updates) => handleUpdateQuestion(index, updates)}
            onSave={() => setEditingIndex(null)}
            onBlur={() =>
              setFieldTouched?.(`${questionsFieldName}[${index}].label`, true)
            }
            error={errors?.[index]?.label}
            touched={touched?.[index]?.label}
            editable={meta.editable ?? true}
            active={meta.active}
            responseCount={meta.responseCount}
            disabledAt={meta.disabledAt}
            isCollapsed={deactivated && !expandedDeactivatedIndices.has(index)}
            onToggleCollapsed={
              deactivated ? () => toggleCollapsed(index) : undefined
            }
            onDeactivate={
              meta.active !== false && (meta.responseCount ?? 0) > 0
                ? onDeactivateRequest
                  ? () =>
                      onDeactivateRequest(index, () => handleDeactivate(index))
                  : () => handleDeactivate(index)
                : undefined
            }
            onActivate={
              deactivated
                ? onActivateRequest
                  ? () => onActivateRequest(index, () => handleActivate(index))
                  : () => handleActivate(index)
                : undefined
            }
            typeLabel={typeLabel}
            questionLabel={questionLabel}
            requiredLabel={requiredLabel}
            questionPreviewLabel={questionPreviewLabel}
            selectTypePlaceholder={selectTypePlaceholder}
            ratingQuestionPlaceholder={ratingQuestionPlaceholder}
            freeTextQuestionPlaceholder={freeTextQuestionPlaceholder}
            removeButton={removeButton}
            defaultRatingMinLabel={defaultRatingMinLabel}
            defaultRatingMaxLabel={defaultRatingMaxLabel}
            ratingTypeLabel={ratingTypeLabel}
            freeTextTypeLabel={freeTextTypeLabel}
            allowedQuestionTypes={allowedQuestionTypes}
            activeBadgeLabel={activeBadgeLabel}
            deactivatedBadgeLabel={deactivatedBadgeLabel}
            responseSummaryText={responseSummaryText}
            deactivateButtonLabel={deactivateButtonLabel}
            activateButtonLabel={activateButtonLabel}
          />
        )
      })}

      {questions.length < questionLimit && (
        <Box className={styles.ButtonWrapper}>
          <Button
            size={'small'}
            startIcon={'add'}
            startIconSize={4}
            variant="secondary"
            shape="oval"
            onClick={handleAddQuestion}
          >
            {addQuestionButton}
          </Button>
        </Box>
      )}
    </Box>
  )
}
